import React, { useEffect } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { AuthKitProvider, useAuth } from "@workos-inc/authkit-react";
import atlasApi from "../utils/atlasapi";
import authApi from "../utils/authApi";
import { setTilesConfig } from "../utils/tilesConfig";

// Pages reachable without an authenticated session.
const EXEMPT_PATHS = ["/forbidden", "/404", "/invalid"];

// The route the user is on, to carry through the auth flow as returnTo.
// Reads window.location rather than useLocation(): this is called from
// callbacks the SDK holds on to, and a closure over a React value could be
// stale by the time it actually runs.
function currentPath() {
	return window.location.pathname + window.location.search;
}

// Wraps AuthKitProvider so onRedirectCallback can use react-router navigation
// (must therefore be rendered inside BrowserRouter).
export function AuthProvider({ children }) {
	const navigate = useNavigate();
	return (
		<AuthKitProvider
			clientId={process.env.REACT_APP_WORKOS_CLIENT_ID}
			onRedirectCallback={({ state }) => {
				// state comes back from the auth flow — treat as untrusted input
				const returnTo = state?.returnTo;
				if (typeof returnTo === "string" && returnTo.startsWith("/") && !returnTo.startsWith("//"))
					navigate(returnTo, { replace: true });
			}}
			onRefresh={({ accessToken }) => atlasApi.setToken(accessToken)}
			// Without returnTo the user lands on window.origin — the SDK's
			// default redirectUri — and onRedirectCallback below has nothing to
			// navigate back to. That is how a refresh failure used to dump you
			// on the front page.
			onRefreshFailure={({ signIn }) => signIn({ state: { returnTo: currentPath() } })}
		>
			{children}
		</AuthKitProvider>
	);
}

// Blocks rendering until the user is authenticated; unauthenticated visitors
// are sent to the hosted AuthKit sign-in page. Also bridges the WorkOS SDK
// into the two plain-JS singletons (atlasApi, authApi).
export default function AuthGate({ children }) {
	const { user, isLoading, signIn, signOut, getAccessToken } = useAuth();
	const location = useLocation();
	const exempt = EXEMPT_PATHS.includes(location.pathname);
	// The SDK can restore `user` from a cached session before it has finished
	// fetching a fresh access token — hold the pages back until one exists,
	// so their mount-time API calls don't race the token exchange.
	const [tokenReady, setTokenReady] = React.useState(false);

	useEffect(() => {
		atlasApi.setTokenProvider(getAccessToken);
		authApi.setWorkosAuth({ user, signOut });
		if (user)
			getAccessToken()
				.then(async (t) => {
					atlasApi.setToken(t);
					// Tileserver URL + API key come from the authenticated
					// config endpoint, never from the build env. Fetched (and
					// cached) before the pages render so map layers can build
					// their tile URLs synchronously. A failure here shouldn't
					// lock the whole app — the map just loses its tile layers.
					try {
						setTilesConfig(await atlasApi.getTileserverConfig());
					} catch (e) {
						console.error("Error fetching tileserver config:", e);
					}
					setTokenReady(true);
				})
				.catch(() => {
					// The SDK restored `user` from a cached session that is no
					// longer valid server-side (e.g. after sign-out) — force a
					// fresh sign-in instead of hanging on the spinner.
					signIn({ state: { returnTo: currentPath() } });
				});
	}, [user, signOut, getAccessToken, signIn, location]);

	useEffect(() => {
		if (!isLoading && !user && !exempt)
			signIn({ state: { returnTo: currentPath() } });
	}, [isLoading, user, exempt, signIn, location]);

	if (exempt) return children;

	if (isLoading || !user || !tokenReady)
		return (
			<div className="d-flex justify-content-center align-items-center vh-100">
				<div className="spinner-border text-secondary" role="status" />
			</div>
		);

	return children;
}

// Route element for /login: kicks off sign-in, or goes home if already signed in.
export function LoginRedirect() {
	const { user, isLoading, signIn } = useAuth();
	const navigate = useNavigate();
	useEffect(() => {
		if (isLoading) return;
		if (user) navigate("/", { replace: true });
		else signIn();
	}, [isLoading, user, signIn, navigate]);
	return null;
}
